/**
 * RecordingSession — event picker + multi-athlete roster for a rotation bout.
 * Coaches pick the event and every athlete in the rotation, then capture each
 * athlete's passes in a row (switching the active athlete between clips).
 */
import React, { useState, useMemo } from 'react';
import { Select } from '../../ui/Input.js';
import Button from '../../ui/Button.js';
import { GYMNASTICS_EVENTS } from '../../../utils/constants.js';

export function RecordingSession({ athletes, onStart, loading }) {
  const [selectedEvent, setSelectedEvent] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [filter, setFilter] = useState('');
  // Multi-camera capture: more than one device records the same event and the
  // clips are grouped (by a shared session code) into a multi-angle set for
  // combined analysis. No calibration/setup — just a second angle on the athlete.
  const [multiCam, setMultiCam] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const sessionCode = useMemo(
    () => Math.random().toString(36).slice(2, 6).toUpperCase(),
    []
  );

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return q ? athletes.filter(a => a.name?.toLowerCase().includes(q)) : athletes;
  }, [athletes, filter]);

  const allFilteredSelected = filtered.length > 0 && filtered.every(a => selectedIds.includes(a.id));

  const toggle = (id) =>
    setSelectedIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);

  const toggleAllFiltered = () =>
    setSelectedIds(ids => allFilteredSelected
      ? ids.filter(id => !filtered.some(a => a.id === id))
      : [...new Set([...ids, ...filtered.map(a => a.id)])]
    );

  const canStart = selectedEvent && selectedIds.length > 0;

  return (
    <div className="flex flex-col gap-4">
      <Select
        label="Event"
        value={selectedEvent}
        onChange={e => setSelectedEvent(e.target.value)}
        required
      >
        <option value="">Select event…</option>
        {GYMNASTICS_EVENTS.map(e => (
          <option key={e.value} value={e.value}>{e.label}</option>
        ))}
      </Select>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-sm font-medium text-gray-700">
            Athletes in this rotation
            {selectedIds.length > 0 && (
              <span className="ml-1.5 text-primary font-semibold">({selectedIds.length})</span>
            )}
          </label>
          {filtered.length > 0 && (
            <button
              type="button"
              onClick={toggleAllFiltered}
              className="text-xs text-primary hover:underline"
            >
              {allFilteredSelected ? 'Clear' : 'Select all'}
            </button>
          )}
        </div>

        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Filter athletes…"
          className="w-full mb-2 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
        />

        <div className="max-h-56 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100">
          {filtered.length === 0 ? (
            <p className="text-sm text-gray-400 p-3">No athletes match.</p>
          ) : (
            filtered.map(a => (
              <label
                key={a.id}
                className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={selectedIds.includes(a.id)}
                  onChange={() => toggle(a.id)}
                  className="w-4 h-4"
                />
                <span className="text-sm text-gray-800">{a.name}</span>
              </label>
            ))
          )}
        </div>
      </div>

      {/* Multi-camera option */}
      <div className="rounded-lg border border-gray-200 p-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={multiCam}
            onChange={e => setMultiCam(e.target.checked)}
            className="w-4 h-4"
          />
          <span className="text-sm font-medium text-gray-800">
            📹 Multi-camera — record this event from more than one device
          </span>
        </label>
        {multiCam && (
          <div className="mt-3 space-y-3 text-sm">
            <p className="text-gray-600">
              Use a second phone for a different angle — both clips are grouped for
              combined (multi-angle) analysis. Just point it at the athlete from
              another side; no calibration needed.
            </p>
            <div>
              <div className="text-xs text-gray-500">This device's session code</div>
              <div className="font-mono text-2xl font-bold tracking-widest text-primary">{sessionCode}</div>
              <p className="text-xs text-gray-500 mt-0.5">
                On the other device, turn on Multi-camera and enter this code below before starting.
              </p>
            </div>
            <div>
              <label className="text-xs text-gray-500">Joining another device? Enter its code</label>
              <input
                type="text"
                value={joinCode}
                onChange={e => setJoinCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                maxLength={6}
                placeholder="e.g. 4F2A"
                className="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-md font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
        )}
      </div>

      <Button
        onClick={() => onStart({
          event: selectedEvent,
          athleteIds: selectedIds,
          // Joining a code overrides this device's own; null when single-camera.
          captureSessionId: multiCam ? (joinCode.trim() || sessionCode) : null,
        })}
        disabled={!canStart}
        loading={loading}
        fullWidth
      >
        Start Rotation ({selectedIds.length})
      </Button>
    </div>
  );
}

export default RecordingSession;
